import React, { useEffect, useState } from "react";
import axios from "axios";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Users2, Plus, Trash2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCabin = () => ({ cabin: "", quantity: 1, guests: 2 });

const GroupBooking = () => {
  const { CABIN_OPTIONS, EVENT } = useCruise();
  const [leader, setLeader] = useState({ leader_name: "", email: "", phone: "", notes: "", promo_code: "" });
  const [cabins, setCabins] = useState([emptyCabin()]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onPromo = (e) => { setDone(false); setLeader((l) => ({ ...l, promo_code: e.detail })); };
    window.addEventListener("hh-promo", onPromo);
    return () => window.removeEventListener("hh-promo", onPromo);
  }, []);

  const updateCabin = (i, field, value) => {
    const next = [...cabins];
    next[i] = { ...next[i], [field]: value };
    setCabins(next);
  };

  const addCabin = () => setCabins([...cabins, emptyCabin()]);
  const removeCabin = (i) => setCabins(cabins.filter((_, idx) => idx !== i));

  const totalCabins = cabins.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
  const totalGuests = cabins.reduce((s, c) => s + (Number(c.guests) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!leader.leader_name || !leader.email) {
      toast.error("Please add the group leader's name and email.");
      return;
    }
    if (!cabins.some((c) => c.cabin)) {
      toast.error("Please select at least one cabin type.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...leader,
        cruise: EVENT.id,
        cabins: cabins
          .filter((c) => c.cabin)
          .map((c) => ({ cabin: c.cabin, quantity: Number(c.quantity) || 1, guests: Number(c.guests) || 2 })),
      };
      await axios.post(`${API}/signups/group`, payload);
      setDone(true);
      toast.success("Group request sent! Our team will reach out to lock in your cabins.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="group" className="relative bg-[#111111] py-24">
      <div className="max-w-4xl mx-auto px-5">
        <div className="text-center mb-12">
          <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-3">Bringing The Crew?</p>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl">
            Group <span className="text-[#E50914]">Booking</span>
          </h2>
          <div className="flex justify-center mt-4">
            <span className="section-line" />
          </div>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">
            Rolling deep? Request multiple cabins in one go and we'll help coordinate your whole group with special
            group rates.
          </p>
        </div>

        {done ? (
          <div className="flex flex-col items-center text-center bg-[#1A1A1A] border border-white/10 rounded-2xl py-14">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#1B8A5A] text-white mb-5">
              <Check size={30} />
            </span>
            <p className="font-cond uppercase tracking-wide text-white text-xl">Group request received!</p>
            <p className="text-white/55 text-sm mt-2 max-w-md">
              We'll be in touch shortly to confirm availability and lock in your group's cabins.
            </p>
            <button
              onClick={() => { setDone(false); setLeader({ leader_name: "", email: "", phone: "", notes: "", promo_code: "" }); setCabins([emptyCabin()]); }}
              className="mt-6 text-[#E50914] font-cond uppercase text-xs tracking-widest hover:underline"
            >
              Submit another group
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-7 sm:p-9 space-y-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914]/15 text-[#E50914]">
                <Users2 size={22} />
              </span>
              <h3 className="font-cond font-bold uppercase text-white text-xl tracking-wide">Group Leader</h3>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Full Name</Label>
                <Input
                  value={leader.leader_name}
                  onChange={(e) => setLeader({ ...leader, leader_name: e.target.value })}
                  placeholder="Your name"
                  className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                />
              </div>
              <div>
                <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Email</Label>
                <Input
                  type="email"
                  value={leader.email}
                  onChange={(e) => setLeader({ ...leader, email: e.target.value })}
                  placeholder="you@email.com"
                  className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                />
              </div>
              <div>
                <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Phone</Label>
                <Input
                  value={leader.phone}
                  onChange={(e) => setLeader({ ...leader, phone: e.target.value })}
                  placeholder="(optional)"
                  className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                />
              </div>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-cond uppercase tracking-widest text-white text-sm">Cabins Requested</h4>
                <span className="font-cond uppercase text-xs tracking-widest text-[var(--hh-gold)]">
                  {totalCabins} cabins • {totalGuests} guests
                </span>
              </div>

              <div className="space-y-3">
                {cabins.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-3 items-end bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
                    <div className="col-span-6 sm:col-span-7">
                      <Label className="text-white/50 font-cond uppercase text-[10px] tracking-widest">Cabin Type</Label>
                      <Select value={c.cabin} onValueChange={(v) => updateCabin(i, "cabin", v)}>
                        <SelectTrigger className="mt-1 bg-[#111111] border-white/15 text-white rounded-lg focus:ring-[#E50914]">
                          <SelectValue placeholder="Select cabin" />
                        </SelectTrigger>
                        <SelectContent>
                          {CABIN_OPTIONS.filter((o) => o !== "Not sure yet").map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-white/50 font-cond uppercase text-[10px] tracking-widest"># Cabins</Label>
                      <Input
                        type="number" min="1"
                        value={c.quantity}
                        onChange={(e) => updateCabin(i, "quantity", e.target.value)}
                        className="mt-1 bg-[#111111] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/50 font-cond uppercase text-[10px] tracking-widest">Guests</Label>
                      <Input
                        type="number" min="1"
                        value={c.guests}
                        onChange={(e) => updateCabin(i, "guests", e.target.value)}
                        className="mt-1 bg-[#111111] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center pb-1">
                      {cabins.length > 1 && (
                        <button type="button" onClick={() => removeCabin(i)} className="text-white/40 hover:text-[#E50914]" aria-label="Remove">
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addCabin}
                className="mt-3 inline-flex items-center gap-2 text-[var(--hh-gold)] font-cond uppercase text-xs tracking-widest hover:underline"
              >
                <Plus size={15} /> Add another cabin
              </button>
            </div>

            <div className="sm:max-w-xs">
              <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Promo Code (optional)</Label>
              <Input
                data-testid="group-promo-input"
                value={leader.promo_code}
                onChange={(e) => setLeader({ ...leader, promo_code: e.target.value.toUpperCase() })}
                placeholder="e.g. SAN2027"
                className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg tracking-widest focus-visible:ring-[#E50914]"
              />
            </div>

            <div>
              <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Notes (optional)</Label>
              <Textarea
                value={leader.notes}
                onChange={(e) => setLeader({ ...leader, notes: e.target.value })}
                placeholder="Tell us about your group, special requests, celebrations, etc."
                className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914] min-h-[90px]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full py-6 h-auto"
            >
              <Users2 size={18} className="mr-2" /> {loading ? "Sending..." : "Request Group Booking"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

export default GroupBooking;
